import { type Handle, css } from 'remix/ui'
import { on } from '#client/event-mixin.ts'
import { readRouterSearch } from '#client/router-location.tsx'
import { normalizeRedirectTo } from '#universal/safe-redirect.ts'
import { colors, spacing, typography } from '#universal/styles/tokens.ts'
import {
	cardCss,
	fieldCss,
	fieldLabelCss,
	getPrimaryButtonCss,
	inputCss,
	mutedLinkCss,
	pageDescriptionCss,
	pageHeaderCss,
	pageTitleCss,
	stackedPageCss,
} from '#universal/styles/style-primitives.ts'
import { fetchPublicAuthConfig } from '#client/social-sign-in.ts'
import { renderHoneypot } from '#client/honeypot-field.tsx'
import {
	readPublicFormProtection,
	renderTurnstileWidgets,
	resetTurnstileWidgets,
	turnstileWidgetClassName,
} from '#client/public-form-protection.ts'

function buildLoginHref(redirectTo: string | null) {
	return redirectTo
		? `/login?redirectTo=${encodeURIComponent(redirectTo)}`
		: '/login'
}

export function VerifyRoute(handle: Handle) {
	let status: 'idle' | 'submitting' = 'idle'
	let message: string | null = null
	let turnstileSiteKey: string | null | undefined

	async function loadProtectionConfig(signal: AbortSignal) {
		if (turnstileSiteKey !== undefined) return
		const config = await fetchPublicAuthConfig(signal)
		if (signal.aborted) return
		turnstileSiteKey = config?.turnstileSiteKey ?? null
		handle.update()
	}

	function getRedirectTo() {
		const params = new URLSearchParams(readRouterSearch(handle))
		return normalizeRedirectTo(params.get('redirectTo'))
	}

	async function handleSubmit(event: SubmitEvent) {
		event.preventDefault()
		if (!(event.currentTarget instanceof HTMLFormElement)) return
		const form = event.currentTarget

		const formData = new FormData(form)
		const code = String(formData.get('code') ?? '').trim()
		const protection = readPublicFormProtection(formData, form)
		if (!code) {
			message = 'Enter the 6-digit code from your authenticator app.'
			handle.update()
			return
		}

		status = 'submitting'
		message = null
		handle.update()

		try {
			const response = await fetch('/verify/2fa.json', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({ code, ...protection }),
			})
			const payload = await response.json().catch(() => null)
			if (!response.ok || !payload?.ok) {
				if (payload?.code === 'expired') {
					window.location.assign(buildLoginHref(getRedirectTo()))
					return
				}
				status = 'idle'
				message =
					typeof payload?.error === 'string'
						? payload.error
						: 'Unable to verify the code.'
				resetTurnstileWidgets()
				handle.update()
				return
			}

			window.location.assign(getRedirectTo() ?? '/account')
		} catch {
			status = 'idle'
			message = 'Network error. Please try again.'
			resetTurnstileWidgets()
			handle.update()
		}
	}

	return () => {
		if (typeof document !== 'undefined' && turnstileSiteKey === undefined) {
			handle.queueTask(loadProtectionConfig)
		}
		if (typeof document !== 'undefined' && turnstileSiteKey) {
			handle.queueTask(() => renderTurnstileWidgets(turnstileSiteKey ?? null))
		}
		const isSubmitting = status === 'submitting'

		return (
			<section mix={css(pageCss)}>
				<header mix={css(pageHeaderCss)}>
					<h1 mix={css(pageTitleCss)}>Two-factor authentication</h1>
					<p mix={css(pageDescriptionCss)}>
						Enter the 6-digit code from your authenticator app to finish signing
						in.
					</p>
				</header>
				<form mix={[css(cardCss), on('submit', handleSubmit)]}>
					{renderHoneypot()}
					<label mix={css(fieldCss)}>
						<span mix={css(fieldLabelCss)}>Verification code</span>
						<input
							type="text"
							name="code"
							required
							autoFocus
							inputMode="numeric"
							autoComplete="one-time-code"
							pattern="[0-9]{6}"
							placeholder="123456"
							mix={css(inputCss)}
						/>
					</label>
					{turnstileSiteKey ? (
						<div class={turnstileWidgetClassName}></div>
					) : null}
					<button
						type="submit"
						disabled={isSubmitting}
						mix={css(primaryButtonCss)}
					>
						{isSubmitting ? 'Verifying...' : 'Verify'}
					</button>
					{message ? (
						<p
							aria-live="polite"
							mix={css({
								color: colors.error,
								fontSize: typography.fontSize.sm,
							})}
						>
							{message}
						</p>
					) : null}
				</form>
				<div mix={css({ display: 'grid', gap: spacing.sm })}>
					<a href={buildLoginHref(getRedirectTo())} mix={css(mutedLinkCss)}>
						Back to login
					</a>
				</div>
			</section>
		)
	}
}

const pageCss = {
	...stackedPageCss,
	maxWidth: '28rem',
	margin: '0 auto',
}

const primaryButtonCss = getPrimaryButtonCss({
	size: 'lg',
	weight: 'semibold',
})
